import { DuplicateActionScheduleType } from '@/components/DuplicateActionModal';
import { COMPLETE } from '@/constants/action';
import { DAY, WEEK, WEEKDAYS } from '@/constants/calendar';
import { CATEGORIES_ICONS } from '@/constants/category';
import { BlockEvent } from '@/gql/blockEvent/types';
import { CategoryEvent } from '@/gql/categoryEvent/types';
import { CronofyProfile } from '@/gql/cronofy/types';
import { RecurrenceData, RecurrenceRule } from '@/gql/events/types';
import { getWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { IconApple, IconExchange, IconGmail, IconLiveConnect, IconOffice365 } from '@/theme/icons';
import { Action, PlannedAction, isPlannedAction, RecurringPeriodGranularity } from '@/types/actions';
import { Block } from '@/types/block';
import { EventStatus, MyEvent } from '@/types/calendar';
import { Category } from '@/types/category';
import { ExternalEventType } from '@/types/myPlan';
import {
  add,
  addDays,
  addYears,
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  differenceInHours,
  differenceInMinutes,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isWithinInterval,
  max,
  min,
  isThisMonth as isThisMonthFns,
  isThisWeek as isThisWeekFns,
  isThisYear as isThisYearFns,
  parse,
  setHours,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { isUndefined } from 'lodash';
import { DateTime, FixedOffsetZone } from 'luxon';
import { dateFnsLocalizer } from 'react-big-calendar';

import { fixUncategorizedName, humanDuration } from '.';
import { convertToMinutes } from './events';

const locales: Record<string, Locale> = {
  'en-US': enUS,
  es: es,
};

const providerIcon = new Map();
providerIcon.set('google', IconGmail);
providerIcon.set('apple', IconApple);
providerIcon.set('exchange', IconExchange);
providerIcon.set('live_connect', IconLiveConnect);
providerIcon.set('office365', IconOffice365);

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (
    date: number | Date,
    options?:
      | {
          locale?: Locale | undefined;
          weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
        }
      | undefined,
  ) => startOfWeek(date, { ...options, weekStartsOn: 1 }),
  getDay,
  locales,
});

const timeReadableConvert = (num: number) => {
  const hours = num / 60;
  const chours = Math.floor(hours);
  const minutes = (hours - chours) * 60;
  const cminutes = Math.round(minutes);

  if (cminutes > 0) {
    return `${chours}h ${cminutes}m`;
  }
  return `${chours}h`;
};

const minutesToDurationString = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  const hoursString = hours < 10 ? `0${hours}` : `${hours}`;
  const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const durationString = `${hoursString}:${minutesString}:00`;
  return durationString;
};

const getEventLength = (event: { end: Date; start: Date }) => {
  return differenceInHours(new Date(event?.end), new Date(event?.start)) >= 1
    ? `${timeReadableConvert(differenceInMinutes(new Date(event?.end), new Date(event?.start)))}`
    : `${differenceInMinutes(new Date(event?.end), new Date(event?.start))}m`;
};

const startOfTheWeek = (date: Date) => {
  return startOfWeek(date, {
    weekStartsOn: 1,
  });
};

const endOfTheWeek = (date: Date) => {
  return endOfWeek(date, {
    weekStartsOn: 1,
  });
};

export const startOfTheMonth = (date: Date) => {
  return startOfMonth(date);
};

export const endOfTheMonth = (date: Date) => {
  return endOfMonth(date);
};

const getEventProvider = (
  currentElement: Record<string, string> | ExternalEventType,
  cronofyProfiles?: CronofyProfile[],
) => {
  return cronofyProfiles?.filter((element) => {
    const found = element?.calendars?.find((el) => currentElement.calendar_id === el.id);

    if (found) {
      return element?.provider;
    }
  });
};

const checkIfIsAllDay = (startDate: string, endDate: string) => {
  return !startDate.includes('T') && !endDate.includes('T');
};

export const checkEventStyles = (status: EventStatus | string, showDeclinedEvents: boolean) => {
  if (status === EventStatus.DECLINED && !showDeclinedEvents) {
    return {
      opacity: 0.3,
      textDecoration: 'line-through',
      textDecorationColor: 'black',
    };
  } else if (status === EventStatus.UNANSWERED) {
    return {
      border: '3px solid white',
    };
  }
};

const externalCalendarAdapterApi = (apiObj: Record<string, any>, cronofyProfiles?: CronofyProfile[]) => {
  return apiObj?.map((el: Record<string, string>, i: number) => {
    const allDay = checkIfIsAllDay(el.start, el.end);
    const eventProvider = getEventProvider(el, cronofyProfiles);
    const hasEventProvider = !isUndefined(eventProvider);

    return {
      id: el.event_uid,
      title: el.summary,
      start: allDay ? formatStringToDate(el.start) : new Date(el.start),
      end: allDay ? endOfDay(setHours(addDays(formatStringToDate(el.end), -1), 0)) : new Date(el.end),
      allDay,
      description: el.description,
      isExternal: true,
      duration: getEventLength({ start: new Date(el.start), end: new Date(el.end) }),
      resource: {
        color: hasEventProvider ? 'gray.400' : 'transparent',
        isExternal: hasEventProvider,
        icon: hasEventProvider ? providerIcon.get(eventProvider?.[0]?.provider) : null,
        provider: eventProvider?.[0]?.provider,
      },
      participationStatus: el.participation_status,
      recurring: el.recurring,
      calendarId: el.calendar_id,
    };
  });
};

const externalCalendarMyEventAdapterApi = (apiObj: any, cronofyProfiles?: CronofyProfile[]): MyEvent[] => {
  if (!apiObj) return [];

  return apiObj.map((el: Record<string, string>) => {
    const allDay = checkIfIsAllDay(el.start, el.end);
    const eventProvider = getEventProvider(el, cronofyProfiles);
    const hasEventProvider = !isUndefined(eventProvider);

    return {
      id: el.event_uid,
      title: el.summary,
      start: allDay ? formatStringToDate(el.start) : new Date(el.start),
      end: allDay ? endOfDay(setHours(addDays(formatStringToDate(el.end), -1), 0)) : new Date(el.end),
      allDay,
      description: el.description,
      isExternal: true,
      duration: getEventLength({ start: new Date(el.start), end: new Date(el.end) }),
      resource: {
        color: hasEventProvider ? 'gray.400' : 'transparent',
        isExternal: hasEventProvider,
        icon: hasEventProvider ? providerIcon.get(eventProvider?.[0]?.provider) : null,
        planned: allDay,
      },
      participationStatus: el.participation_status,
      recurring: el.recurring,
      calendarId: el.calendar_id,
    };
  });
};

const externalEventFormatDuration = (differenceInMinutes: number) => {
  const hours = Math.floor(differenceInMinutes / 60);
  const minutes = differenceInMinutes % 60;
  const seconds = 0;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

const calendarAdapterApi = (action: PlannedAction): MyEvent => {
  // After upgrading date-fns, a new bug appeared in the code, making us realise
  // that this function was being called with actions that had scheduledDate set
  // to null (thanks to a lot of unsafe typescript "as" usages). Previously the
  // utcToLocalDate function was returning Invalid Date, and for some reason the
  // app managed to work with it. But the new date-fns makes utcToLocalDate
  // throw error and crash the app.
  // I tried to improve the types and fix the code so this function would only
  // be called with planned actions, but I couldn't fix all the instances, as
  // that requires a really huge refactor, very risky, basically a full
  // rewrite of the way we generate events to show on the calendars.
  // So for the time being I decided to just log the invalid calls and restore
  // the old broken but functioning behaviour. Hopefully one day we'll rewrite
  // this whole thing with better code.
  let localDate;
  if (action && isPlannedAction(action)) {
    localDate = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);
  } else {
    console.error('Invalid action passed to calendarAdapterApi', action);
    localDate = new Date(NaN);
  }

  return {
    id: action?.id,
    eventId: action?.event?.id,
    allDay: false,
    description: '',
    title: action?.title,
    start: localDate,
    end: add(localDate, { minutes: convertToMinutes(action.duration) }),
    noScheduledTime: action.scheduledTime === null || !action.scheduledTime,
    duration: humanDuration(convertToMinutes(action.duration) * 60),
    status: action?.progressStatus,
    resource: {
      color: action?.category?.color || 'gray.400',
      isExternal: !!action.isExternal,
      icon: action.isExternal
        ? (action.icon as React.FunctionComponent<React.SVGProps<SVGSVGElement>>)
        : CATEGORIES_ICONS?.[action?.category?.icon ?? 'uncategorized'],
      planned: action.scheduledTime === null,
    },
    dateOfStarring: action.dateOfStarring,
    isLocked: action.isLocked,
    __metadata: {
      action,
    },
  };
};

/**
 * Advance a recurrence cursor to the latest occurrence-aligned date <= the day BEFORE
 * rangeStart (see the pad note in the body), in O(1), preserving the cursor's time-of-day
 * (timed occurrence keys derive from it via localDateToUTC). Only valid when the rule has no occurrence cap (count): capped rules must
 * enumerate from the series start to know where the cap lands. Weekly rules with by_day
 * land on the Monday of an aligned week (the expansion loops re-enter later weeks from
 * Monday). Monthly/yearly cursors are returned unchanged — they advance fast enough that
 * skipping is not worth the alignment risk.
 *
 * exceptionDates: pass the rule's exception dates when the calling loop's exception branch
 * advances the cursor NON-uniformly (generateInstances and getOccurrences step +1 day /
 * +incrementInterval on exception days, permanently phase-shifting some rule shapes). Any
 * exception before rangeStart then disables the skip so the walk reproduces the historical
 * shift exactly. Loops that advance uniformly on exception days (makeEvents, the weekly-plan
 * items memo) omit it.
 */
export function fastForwardRecurrenceCursor(
  cursor: Date,
  rangeStart: Date,
  frequency: string | undefined,
  interval: number,
  byDay?: { day: string }[],
  exceptionDates?: Date[],
): Date {
  // Land one day BEFORE rangeStart: createInstance's stored-vs-current gmtOffset correction
  // can pull an instance generated from the previous candidate day across midnight into the
  // window (DST-spanning timed series). Callers post-filter, so the extra day cannot leak.
  const target = subDays(rangeStart, 1);
  if (!isBefore(cursor, target)) {
    return cursor;
  }
  if (exceptionDates?.some((d) => d instanceof Date && !Number.isNaN(d.getTime()) && isBefore(d, target))) {
    return cursor;
  }
  const step = Math.max(1, Math.floor(interval) || 1);
  const normalizedFreq = frequency?.toLowerCase?.();
  if (normalizedFreq === 'daily' || normalizedFreq === 'day') {
    const behind = differenceInCalendarDays(target, cursor);
    return add(cursor, { days: Math.floor(behind / step) * step });
  }
  if (normalizedFreq === 'weekly' || normalizedFreq === 'week') {
    if (byDay?.length) {
      // The expansion loops walk day-by-day to the first by_day match and anchor the
      // interval-week phase on THAT day's week, which is the week AFTER the cursor's
      // when the cursor falls past the last by_day weekday of its own week.
      let firstValid: Date | null = null;
      for (let i = 0; i < 7; i++) {
        const candidate = addDays(cursor, i);
        if (isValidWeeklyDay(candidate, byDay)) {
          firstValid = candidate;
          break;
        }
      }
      if (!firstValid) {
        return cursor;
      }
      const behind = differenceInCalendarWeeks(target, firstValid, { weekStartsOn: 1 });
      const skip = Math.floor(behind / step) * step;
      if (skip <= 0) {
        return cursor;
      }
      // Jump to Monday of the aligned week the same way the loops do (-daysFromMonday +
      // whole weeks from a walked day): add() keeps the cursor's clock time, which
      // startOfWeek would zero out.
      const daysFromMonday = (firstValid.getDay() + 6) % 7;
      return add(firstValid, { days: -daysFromMonday + skip * 7 });
    }
    const behind = differenceInCalendarWeeks(target, cursor, { weekStartsOn: 1 });
    const skip = Math.floor(behind / step) * step;
    if (skip <= 0) {
      return cursor;
    }
    return add(cursor, { weeks: skip });
  }
  return cursor;
}

export function generateInstances(action: any, endDate?: Date, rangeStart?: Date) {
  const instances = [];
  const { event } = action;

  if (!event?.recurrence?.rules) {
    return calendarAdapterApi(action);
  }

  const { recurrence: eventEndDate } = event;
  const [fullDate] = (event?.start ?? '').split('T');
  const fullEndDate = eventEndDate?.rules?.[0]?.until;

  const start = utcToLocalDate(fullDate, action.scheduledTime, action.timezone, action.gmtOffset);
  const until = fullEndDate
    ? utcToLocalDate(fullEndDate, action.scheduledTime, action.timezone, action.gmtOffset)
    : endDate;

  let currentDate = start;
  const rules = event?.recurrence?.rules?.[0];
  const interval = rules?.interval || 1;
  const maxOccurrences = rules?.count;
  const validDays = rules?.by_day;
  const isWeeklyRecurrence = rules?.frequency === 'weekly';

  // Returns null for empty rules or an unknown/empty frequency ('' or null) -> treat as non-recurring.
  const incrementInterval = getIncrementInterval(rules);
  if (!incrementInterval) return calendarAdapterApi(action);

  const exceptions = getExceptionDates(eventEndDate, action);
  const customs = eventEndDate.exceptions?.custom;
  let occurrenceCount = 0;
  let safety = 0;

  // Callers that only consume occurrences on/after rangeStart pass it so long-running
  // series don't enumerate their entire history. Skipped for capped rules (count).
  // Exceptions only shift this loop's phase for weekly by_day interval>1 (the exception
  // branch advances {days:1} instead of taking the interval-week jump); every other shape
  // advances identically on exception days, so it keeps the clamp.
  if (rangeStart && !maxOccurrences) {
    const exceptionsCanShiftPhase = isWeeklyRecurrence && Boolean(validDays?.length) && interval > 1;
    currentDate = fastForwardRecurrenceCursor(
      currentDate,
      rangeStart,
      rules?.frequency,
      interval,
      validDays,
      exceptionsCanShiftPhase ? exceptions : undefined,
    );
  }

  while (
    (!until || isBefore(currentDate, until) || isEqual(currentDate, until)) &&
    (!maxOccurrences || occurrenceCount < maxOccurrences) &&
    safety++ < 10000 // safety cap: never loop forever on malformed recurrence data
  ) {
    if (isExceptionDate(currentDate, exceptions)) {
      currentDate = add(currentDate, incrementInterval);
      continue;
    }

    if (isWeeklyRecurrence && validDays?.length && !isValidWeeklyDay(currentDate, validDays)) {
      currentDate = add(currentDate, { days: 1 });
      continue;
    }

    instances.push(createInstance(action, currentDate, customs));
    occurrenceCount++;

    if (shouldIncrementByWeek(currentDate, validDays) && isWeeklyRecurrence && interval > 1) {
      const daysFromMonday = (currentDate.getDay() + 6) % 7;
      currentDate = add(currentDate, { days: -daysFromMonday + interval * 7 });
    } else {
      currentDate = add(currentDate, incrementInterval);
    }
  }

  return instances;
}

export type RecurringOccurrenceTooltipInfo = {
  nextStart: Date | null;
  completedCount: number;
  noScheduledTime: boolean;
  lineFormat?: 'default' | 'weeklyNext' | 'weeklyPlain';
};

/**
 * Next upcoming **incomplete** instance (start >= now) and count of past completed instances,
 * for recurring action tooltips (e.g. Category Manager clock icon). Skips occurrences already marked complete.
 */
export function getRecurringOccurrenceTooltipInfo(
  action: Action,
  now: Date = new Date(),
): RecurringOccurrenceTooltipInfo | null {
  if (!action?.event?.recurrence?.rules?.length) {
    return null;
  }

  const horizon = addYears(now, 2);

  // Bounded fast path — the full scan below walks from the series start to now+2y
  // (thousands of iterations for an old daily series) and runs per recurring row on many
  // pages. When the series' own status is not COMPLETE: the next incomplete occurrence is
  // findable in a short forward window, and past completions can only exist on explicit
  // exceptions.custom dates (see createInstance), so their count is computable from that
  // span. Any case the window can't answer falls back to the full scan, so the output is
  // identical.
  if (recurringSeriesDefaultProgressStatus(action) !== COMPLETE) {
    // 62 days covers daily/weekly/monthly rules at small intervals; sparse rules
    // (yearly, large intervals, ended series) yield nothing and fall through.
    const nearRaw = generateInstances(action, endOfDay(addDays(now, 62)), now);
    const near: MyEvent[] = Array.isArray(nearRaw) ? nearRaw : [nearRaw];
    const next =
      [...near]
        .sort((a, b) => a.start.getTime() - b.start.getTime())
        .find((i) => !isBefore(i.start, now) && !isRecurringSeriesInstanceComplete(action, i)) ?? null;

    if (next) {
      let completedCount = 0;
      const completeDates = getExplicitCustomCompleteDates(action).filter((d) => isBefore(d, now));
      if (completeDates.length > 0) {
        // Same span bounds (and +1-day pad) as the bounded last-completed scan.
        const spanRaw = generateInstances(
          action,
          endOfDay(addDays(max(completeDates), 1)),
          startOfDay(min(completeDates)),
        );
        const span: MyEvent[] = Array.isArray(spanRaw) ? spanRaw : [spanRaw];
        completedCount = span.filter(
          (i) => isBefore(i.start, now) && isRecurringSeriesInstanceComplete(action, i),
        ).length;
      }

      return {
        nextStart: next.start,
        completedCount,
        noScheduledTime: Boolean(next.noScheduledTime ?? !action.scheduledTime),
      };
    }
  }

  const instancesRaw = generateInstances(action, horizon);
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];

  if (!instances.length) {
    return null;
  }

  const sorted = [...instances].sort((a, b) => a.start.getTime() - b.start.getTime());
  const next =
    sorted.find((i) => !isBefore(i.start, now) && !isRecurringSeriesInstanceComplete(action, i)) ?? null;
  const completedCount = sorted.filter(
    (i) => isBefore(i.start, now) && isRecurringSeriesInstanceComplete(action, i),
  ).length;

  return {
    nextStart: next?.start ?? null,
    completedCount,
    noScheduledTime: Boolean(next?.noScheduledTime ?? !action.scheduledTime),
  };
}

export type RecurringCompletedSeriesStats = {
  completedCount: number;
  lastCompletedStart: Date;
  noScheduledTime: boolean;
};

/**
 * Completed occurrences: `lastCompletedStart` is the latest completed instance in the series (generated horizon).
 * `completedCount` prefers explicit per-date completes in exceptions.custom; otherwise falls back to completed instance count (capped).
 */
export function getRecurringCompletedSeriesStats(
  action: Action,
  now: Date = new Date(),
): RecurringCompletedSeriesStats | null {
  if (!action?.event?.recurrence?.rules?.length) {
    return null;
  }

  const horizon = addYears(now, 2);
  const lastCompletedStart = getRecurringSeriesLastCompletedOccurrenceStart(action, horizon);
  if (!lastCompletedStart) {
    return null;
  }

  let completedCount = 0;
  const custom = action.event.recurrence.exceptions?.custom;
  if (custom && typeof custom === 'object') {
    for (const val of Object.values(custom)) {
      if (val && typeof val === 'object' && 'progressStatus' in val && val.progressStatus === COMPLETE) {
        completedCount++;
      }
    }
  }
  if (completedCount === 0) {
    // Fallback only when no explicit per-date completes exist: the full series walk
    // (start -> now+2y) is expensive for old series, so it must not run unconditionally —
    // this renders per completed recurring row on the capture lists.
    const instancesRaw = generateInstances(action, horizon);
    const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
    const completedInstances = instances.filter((i) => isRecurringSeriesInstanceComplete(action, i));
    completedCount = Math.min(completedInstances.length, 366);
  }

  return {
    completedCount,
    lastCompletedStart,
    noScheduledTime: !action.scheduledTime,
  };
}

export type RecurringWeekPeriodKind = 'past' | 'current' | 'future';

/**
 * Classify the selected calendar week relative to "today" for recurring copy (Next / Last / plain).
 */
export function classifyRecurringWeekPeriodKind(
  weekStart: Date,
  weekEnd: Date,
  now: Date = new Date(),
): RecurringWeekPeriodKind {
  const todayStart = startOfDay(now);
  if (isBefore(endOfDay(weekEnd), todayStart)) {
    return 'past';
  }
  if (isAfter(startOfDay(weekStart), endOfDay(now))) {
    return 'future';
  }
  return 'current';
}

export type RecurringWeeklyViewModel = {
  periodKind: RecurringWeekPeriodKind;
  activeInWeekCount: number;
  completedInWeekCount: number;
  activeTooltipStart: Date | null;
  completedTooltipStart: Date | null;
  noScheduledTime: boolean;
  granularity: RecurringPeriodGranularity;
};

const MAX_RECURRING_OCCURRENCES_BY_GRANULARITY: Record<RecurringPeriodGranularity, number> = {
  day: 1,
  week: 7,
  month: 31,
  year: 366,
};

export function capRecurringOccurrencesForDisplay(
  granularity: RecurringPeriodGranularity,
  count: number,
): number {
  return Math.min(count, MAX_RECURRING_OCCURRENCES_BY_GRANULARITY[granularity]);
}

function instanceInWeek(instance: MyEvent, weekStart: Date, weekEnd: Date): boolean {
  const interval = { start: startOfDay(weekStart), end: endOfDay(weekEnd) };
  return isWithinInterval(instance.start, interval);
}

export function isRecurrenceOccurrenceExplicitlyComplete(action: Action, instanceStart: Date): boolean {
  // Day-key Set lookup: same-local-calendar-day comparison, identical to
  // isSameDay against each parsed date but O(1) per check.
  return getExplicitCustomCompletions(action).dayKeys.has(format(instanceStart, 'yyyy-MM-dd'));
}

export function isRecurringSeriesInstanceComplete(action: Action, instance: MyEvent): boolean {
  if (instance.status === COMPLETE) {
    return true;
  }
  return isRecurrenceOccurrenceExplicitlyComplete(action, instance.start);
}

type ExplicitCompletions = { dates: Date[]; dayKeys: Set<string> };
const EMPTY_COMPLETIONS: ExplicitCompletions = { dates: [], dayKeys: new Set() };

/**
 * A checked-off year-old daily habit holds ~365 exceptions.custom entries, each parsed with
 * a luxon timezone conversion. Completeness checks run per generated instance, several times
 * per action, per render — without this cache that is ~100k+ conversions per render of a
 * busy weekly plan (the June-24 regression's dominant cost). Keyed by the custom map's
 * object identity: recurrence writes replace the object (cache-safe); the react-query cache
 * is no longer mutated in place.
 */
const explicitCompletionsCache = new WeakMap<object, ExplicitCompletions>();

/**
 * exceptions.custom entries explicitly marked COMPLETE, as parsed local start dates plus a
 * local-day-key Set for O(1) completeness checks. For a series whose own status is not
 * COMPLETE these are the only dates that can hold a completed occurrence (see
 * createInstance / isRecurringSeriesInstanceComplete), so full-history scans can be
 * bounded to their span.
 */
function getExplicitCustomCompletions(action: Action): ExplicitCompletions {
  const custom = action?.event?.recurrence?.exceptions?.custom;
  if (!custom || typeof custom !== 'object') {
    return EMPTY_COMPLETIONS;
  }

  const cached = explicitCompletionsCache.get(custom);
  if (cached) {
    return cached;
  }

  const dates: Date[] = [];
  const dayKeys = new Set<string>();
  for (const [dateKey, val] of Object.entries(custom)) {
    if (
      !val ||
      typeof val !== 'object' ||
      (val as { progressStatus?: string }).progressStatus !== COMPLETE
    ) {
      continue;
    }
    try {
      const d = utcToLocalDate(dateKey, action.scheduledTime, action.timezone, action.gmtOffset);
      if (!Number.isNaN(d.getTime())) {
        dates.push(d);
        dayKeys.add(format(d, 'yyyy-MM-dd'));
      }
    } catch {
      continue;
    }
  }
  const result: ExplicitCompletions = { dates, dayKeys };
  explicitCompletionsCache.set(custom, result);
  return result;
}

function getExplicitCustomCompleteDates(action: Action): Date[] {
  return getExplicitCustomCompletions(action).dates;
}

export function getRecurringSeriesLastCompletedOccurrenceStart(
  action: Action,
  horizonEnd: Date = addYears(new Date(), 2),
): Date | null {
  if (!action?.event?.recurrence?.rules?.length) {
    return null;
  }

  // A series whose own status is not COMPLETE can only have completed occurrences on the
  // explicit custom-exception dates, so instance generation is bounded to their span
  // instead of the whole series history (this runs per row on the capture lists).
  if (recurringSeriesDefaultProgressStatus(action) !== COMPLETE) {
    const completeDates = getExplicitCustomCompleteDates(action);
    if (completeDates.length === 0) {
      return null;
    }
    // +1 day: for untimed actions in negative-UTC-offset runtimes, the instance matching
    // custom key K is generated from candidate day K but its start parses back to K-1
    // local, so a span ending exactly at max(completeDates) would cut that candidate off.
    const spanEnd = endOfDay(addDays(max(completeDates), 1));
    const boundedEnd = isBefore(spanEnd, horizonEnd) ? spanEnd : horizonEnd;

    // Tail-first: the answer is the LATEST completed occurrence and completions cannot
    // exceed max(completeDates), so a short window ending there almost always contains
    // it. A fully-checked-off year-old habit's custom span IS its whole history — walking
    // it per call was the residual per-render cost. Fall back to the full span only when
    // the tail has no real completed occurrence (e.g. stale off-day entries at the end).
    const tailRaw = generateInstances(action, boundedEnd, startOfDay(subDays(max(completeDates), 35)));
    const tail: MyEvent[] = Array.isArray(tailRaw) ? tailRaw : [tailRaw];
    const tailCompleted = tail.filter((i) => isRecurringSeriesInstanceComplete(action, i));
    if (tailCompleted.length > 0) {
      return max(tailCompleted.map((i) => i.start));
    }

    const instancesRaw = generateInstances(action, boundedEnd, startOfDay(min(completeDates)));
    const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
    const completed = instances.filter((i) => isRecurringSeriesInstanceComplete(action, i));
    return completed.length === 0 ? null : max(completed.map((i) => i.start));
  }

  // Series-complete rows: every generated instance inherits COMPLETE, so the answer is the
  // last occurrence before the horizon. Scan a short tail window first — the full walk from
  // the series start (potentially years) is only needed when the tail is empty (series
  // ended long ago, sparse rules) or every tail instance was explicitly un-completed.
  const tailRaw = generateInstances(action, horizonEnd, subDays(horizonEnd, 66));
  const tail: MyEvent[] = Array.isArray(tailRaw) ? tailRaw : [tailRaw];
  const tailCompleted = tail.filter((i) => isRecurringSeriesInstanceComplete(action, i));
  if (tailCompleted.length > 0) {
    return max(tailCompleted.map((i) => i.start));
  }

  const instancesRaw = generateInstances(action, horizonEnd);
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
  const completed = instances.filter((i) => isRecurringSeriesInstanceComplete(action, i));
  if (completed.length === 0) {
    return null;
  }

  return max(completed.map((i) => i.start));
}

/**
 * Recurring counts and tooltips for a bounded period (day / week / month / year).
 * Raw counts are uncapped; use capRecurringOccurrencesForDisplay for badge display.
 * Completed counts use explicit per-date exceptions only, not inherited row status.
 */
export function getRecurringPeriodViewModel(
  action: Action,
  periodStart: Date,
  periodEnd: Date,
  now: Date = new Date(),
  granularity: RecurringPeriodGranularity = 'week',
): RecurringWeeklyViewModel | null {
  if (!action?.event?.recurrence?.rules?.length) {
    return null;
  }

  const periodKind = classifyRecurringWeekPeriodKind(periodStart, periodEnd, now);
  const instancesRaw = generateInstances(action, endOfDay(periodEnd), startOfDay(periodStart));
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
  const inPeriod = instances.filter((i) => instanceInWeek(i, periodStart, periodEnd));

  const inPeriodWithComplete = inPeriod.map((i) => ({
    instance: i,
    complete: isRecurringSeriesInstanceComplete(action, i),
  }));
  const complete = inPeriodWithComplete.filter((x) => x.complete).map((x) => x.instance);
  const incomplete = inPeriodWithComplete.filter((x) => !x.complete).map((x) => x.instance);

  const activeInWeekCount = incomplete.length;
  const completedInWeekCount = complete.length;
  const noScheduledTime = !action.scheduledTime;

  let activeTooltipStart: Date | null = null;
  if (incomplete.length > 0) {
    if (granularity === 'day') {
      activeTooltipStart = min(incomplete.map((i) => i.start));
    } else if (periodKind === 'past') {
      activeTooltipStart = max(incomplete.map((i) => i.start));
    } else if (periodKind === 'current') {
      const fromTodayOnward = incomplete.filter((i) => !isBefore(startOfDay(i.start), startOfDay(now)));
      activeTooltipStart =
        fromTodayOnward.length > 0 ? min(fromTodayOnward.map((i) => i.start)) : min(incomplete.map((i) => i.start));
    } else {
      activeTooltipStart = min(incomplete.map((i) => i.start));
    }
  }

  let completedTooltipStart: Date | null = null;
  if (complete.length > 0) {
    if (granularity === 'day') {
      completedTooltipStart = max(complete.map((i) => i.start));
    } else if (periodKind === 'future') {
      completedTooltipStart = min(complete.map((i) => i.start));
    } else {
      completedTooltipStart = max(complete.map((i) => i.start));
    }
  }

  return {
    periodKind,
    activeInWeekCount,
    completedInWeekCount,
    activeTooltipStart,
    completedTooltipStart,
    noScheduledTime,
    granularity,
  };
}

export function getRecurringWeeklyViewModel(
  action: Action,
  weekStart: Date,
  weekEnd: Date,
  now: Date = new Date(),
): RecurringWeeklyViewModel | null {
  return getRecurringPeriodViewModel(action, weekStart, weekEnd, now, 'week');
}

export function hasRecurringOccurrenceInRange(action: Action, rangeStart: Date, rangeEnd: Date): boolean {
  if (!action?.event?.recurrence?.rules?.length) {
    return false;
  }

  const instancesRaw = generateInstances(action, endOfDay(rangeEnd), startOfDay(rangeStart));
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];

  return instances.some((i) => instanceInWeek(i, rangeStart, rangeEnd));
}

export type RecurringInstancesInRangeSummary = {
  inRangeCount: number;
  completedInRangeCount: number;
  allCompleteInRange: boolean;
  hasCompleteInRange: boolean;
};

/**
 * Instances in [rangeStart, rangeEnd] and whether each is complete. Uses
 * {@link isRecurringSeriesInstanceComplete} (instance status + customs) so “all done in range”
 * matches generated instances, not only explicit exception keys.
 */
export function getRecurringInstancesInRangeCompletionSummary(
  action: Action,
  rangeStart: Date,
  rangeEnd: Date,
): RecurringInstancesInRangeSummary {
  const empty: RecurringInstancesInRangeSummary = {
    inRangeCount: 0,
    completedInRangeCount: 0,
    allCompleteInRange: false,
    hasCompleteInRange: false,
  };

  if (!action?.event?.recurrence?.rules?.length) {
    return empty;
  }

  const instancesRaw = generateInstances(action, endOfDay(rangeEnd), startOfDay(rangeStart));
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
  const inRange = instances.filter((i) => instanceInWeek(i, rangeStart, rangeEnd));

  if (inRange.length === 0) {
    return empty;
  }

  const hasCompleteInRange = inRange.some((i) => isRecurringSeriesInstanceComplete(action, i));
  const completedInRangeCount = inRange.filter((i) => isRecurringSeriesInstanceComplete(action, i)).length;

  return {
    inRangeCount: inRange.length,
    completedInRangeCount,
    allCompleteInRange: inRange.every((i) => isRecurringSeriesInstanceComplete(action, i)),
    hasCompleteInRange,
  };
}

export function getRecurringOccurrenceExceptionCustomKey(action: Action, instanceLocalStart: Date): string {
  if (action.scheduledTime) {
    return localDateToUTC(instanceLocalStart).scheduledDate;
  }
  return formatDateToString(instanceLocalStart);
}

export function mergeRecurrenceCompletingAllInstancesInRange(
  action: Action,
  rangeStart: Date,
  rangeEnd: Date,
): RecurrenceData | null {
  if (!action?.event?.recurrence?.rules?.length) {
    return null;
  }

  const instancesRaw = generateInstances(action, endOfDay(rangeEnd), startOfDay(rangeStart));
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
  const inRange = instances.filter((i) => instanceInWeek(i, rangeStart, rangeEnd));
  const toComplete = inRange.filter((i) => !isRecurringSeriesInstanceComplete(action, i));

  if (toComplete.length === 0) {
    return null;
  }

  const recurrence = structuredClone(action.event.recurrence) as RecurrenceData;
  recurrence.exceptions ??= {};
  recurrence.exceptions.custom ??= {};

  for (const inst of toComplete) {
    const key = getRecurringOccurrenceExceptionCustomKey(action, inst.start);
    const existing = recurrence.exceptions.custom![key];
    recurrence.exceptions.custom![key] = {
      ...(existing && typeof existing === 'object' ? existing : {}),
      progressStatus: COMPLETE,
    };
  }

  return recurrence;
}

export const MAX_REFLECTION_RECURRING_COMPLETED_DATES = 7;

export function getRecurringCompletedInstanceStartsInPeriod(
  action: Action,
  periodStart: Date,
  periodEnd: Date,
  maxDates: number = MAX_REFLECTION_RECURRING_COMPLETED_DATES,
): Date[] {
  if (!action?.event?.recurrence?.rules?.length) {
    return [];
  }

  const instancesRaw = generateInstances(action, endOfDay(periodEnd), startOfDay(periodStart));
  const instances: MyEvent[] = Array.isArray(instancesRaw) ? instancesRaw : [instancesRaw];
  const completedInPeriod = instances
    .filter((i) => instanceInWeek(i, periodStart, periodEnd) && isRecurringSeriesInstanceComplete(action, i))
    .map((i) => i.start)
    .sort((a, b) => b.getTime() - a.getTime());

  return completedInPeriod.slice(0, maxDates);
}

const weekdayMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export function alignLocalDatetimeToFirstWeeklyByDay(localDateTime: Date, byDays: { day: string }[]): Date {
  if (!byDays?.length) {
    return localDateTime;
  }
  const allowed = new Set<number>();
  for (const entry of byDays) {
    const key = (entry?.day || '').trim().toLowerCase();
    const wd = weekdayMap[key];
    if (wd !== undefined) {
      allowed.add(wd);
    }
  }
  if (allowed.size === 0) {
    return localDateTime;
  }
  let cur = new Date(localDateTime.getTime());
  if (allowed.has(cur.getDay())) {
    return cur;
  }
  for (let i = 0; i < 7; i++) {
    cur = addDays(cur, 1);
    if (allowed.has(cur.getDay())) {
      return cur;
    }
  }
  return localDateTime;
}

export function weeklyByDayAnchorNeedsAlignment(localDateTime: Date, byDays: { day: string }[]): boolean {
  const aligned = alignLocalDatetimeToFirstWeeklyByDay(localDateTime, byDays);
  return aligned.getTime() !== localDateTime.getTime();
}

export function getIncrementInterval(rules: any) {
  const interval = rules?.interval || 1;
  switch (rules?.frequency) {
    case 'daily':
      return { days: interval };
    case 'weekly':
      return rules?.by_day?.length ? { days: 1 } : { weeks: interval };
    case 'monthly':
      return { months: interval };
    case 'yearly':
      return { years: interval };
    default:
      return null;
  }
}

export function getExceptionDates(eventEndDate: any, action: any) {
  return (eventEndDate.exceptions?.add || []).map((exception: { date: string }) =>
    utcToLocalDate(exception.date, action.scheduledTime, action.timezone, action.gmtOffset),
  );
}

export function isExceptionDate(date: Date, exceptions: Date[]) {
  return exceptions.some((exception) => isSameDay(date, exception));
}

export function isValidWeeklyDay(date: Date, validDays: any[]) {
  return validDays.map((d: { day: string }) => weekdayMap[d.day]).includes(date.getDay());
}

export function shouldIncrementByWeek(currentDate: Date, validDays: any[]) {
  const toEndOfWeekNum = (n: number) => (n === 0 ? 7 : n);
  const validDayNumbers = (validDays?.map((d: { day: string }) => toEndOfWeekNum(weekdayMap[d.day])) || []).filter(
    (n) => !Number.isNaN(n),
  );
  if (!validDayNumbers.length) return false;
  return toEndOfWeekNum(currentDate.getDay()) === Math.max(...validDayNumbers);
}

/** Series-level default for generated instances; shadow rows override progressStatus for UI only. */
function recurringSeriesDefaultProgressStatus(action: any): string | undefined {
  if (action?.isRecurringSeriesCompletedRow && action.recurringSeriesRowOriginalProgressStatus != null) {
    return action.recurringSeriesRowOriginalProgressStatus;
  }
  return action?.progressStatus;
}

function createInstance(action: any, date: Date, customs: any) {
  // Match myplan makeEvents: customs keys are localDateToUTC(...).scheduledDate when timed,
  // else calendar yyyy-MM-dd — not UTC slice (that misses per-occurrence completion).
  const scheduledDate = action.scheduledTime
    ? localDateToUTC(date).scheduledDate
    : formatDateToString(date);
  const progressStatus =
    customs?.[scheduledDate]?.progressStatus ?? recurringSeriesDefaultProgressStatus(action);
  const dateOfStarring = customs?.[scheduledDate]?.dateOfStarring ?? action?.dateOfStarring;
  const localDate = utcToLocalDate(scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);

  return {
    id: `${action.id}-${format(localDate, 'yyyy-MM-dd')}`,
    eventId: action?.id,
    allDay: false,
    description: '',
    title: action?.title,
    start: localDate,
    end: add(localDate, { minutes: convertToMinutes(action.duration) }),
    noScheduledTime: !action.scheduledTime,
    duration: humanDuration(convertToMinutes(action.duration) * 60),
    status: progressStatus,
    resource: {
      color: action?.category?.color || 'gray.400',
      isExternal: !!action.isExternal,
      icon: action.isExternal ? action.icon : CATEGORIES_ICONS?.[action?.category?.icon ?? 'uncategorized'],
      planned: !action.scheduledTime,
    },
    dateOfStarring,
    isLocked: action.isLocked,
    __metadata: { action: { ...action, scheduledDate, progressStatus, dateOfStarring } },
  };
}

export function expandRecurringActions(actions: any, endDate?: Date, rangeStart?: Date) {
  return actions.flatMap((action: any) => generateInstances(action, endDate, rangeStart));
}

const categoryEventAdapterApi = (categoryEvent: CategoryEvent, category?: Category): MyEvent => {
  const localDate = utcToLocalDate(
    categoryEvent.scheduledDate,
    categoryEvent.scheduledTime,
    categoryEvent.timezone,
    categoryEvent.gmtOffset,
  );

  return {
    id: categoryEvent.id,
    title: fixUncategorizedName(category?.name ?? '[UNKNOWN]'),
    description: '',
    start: localDate,
    end: add(localDate, { minutes: convertToMinutes(categoryEvent.duration) }),
    duration: humanDuration(convertToMinutes(categoryEvent.duration) * 60),
    allDay: false,
    resource: {
      color: category?.color || 'gray.400',
      icon: CATEGORIES_ICONS[category?.icon ?? 'uncategorized'],
      isExternal: false,
      planned: false,
    },
    dateOfStarring: null,
    isLocked: false,
    __metadata: {
      categoryEvent,
    },
  };
};

const blockEventAdapterApi = (blockEvent: BlockEvent, block?: Block): MyEvent => {
  const localDate = utcToLocalDate(
    blockEvent.scheduledDate,
    blockEvent.scheduledTime,
    blockEvent.timezone,
    blockEvent.gmtOffset,
  );

  return {
    id: blockEvent.id,
    title: block?.result ?? '',
    description: '',
    start: localDate,
    end: add(localDate, { minutes: convertToMinutes(blockEvent.duration) }),
    duration: humanDuration(convertToMinutes(blockEvent.duration) * 60),
    allDay: false,
    resource: {
      color: block?.category?.color || 'gray.400',
      icon: CATEGORIES_ICONS[block?.category?.icon ?? 'uncategorized'],
      isExternal: false,
      planned: false,
    },
    dateOfStarring: null,
    isLocked: false,
    __metadata: {
      blockEvent,
    },
  };
};

const formatDateToString = (date: Date) => {
  return format(date, 'yyyy-MM-dd');
};

const formatStringToDate = (date: string) => {
  if (/[TZ]/.test(date)) {
    return new Date(date);
  }

  return parse(date, 'yyyy-MM-dd', new Date());
};

// Pure function of its four (string) args, but each call pays luxon IANA-zone offset
// lookups and it runs per occurrence of every timed action on every render — the bulk
// of the ~1s main-thread stall when marking an action done on a busy week. Memoized on
// the arg tuple; keyed on Intl.DateTimeFormat identity so mocked-timezone tests stay
// correct. Timestamps (not Date objects) are cached so every caller gets a fresh Date.
const utcToLocalDateCache = new Map<string, number>();
let utcToLocalDateCacheCtor: unknown;

const utcToLocalDate = (
  scheduledDate: string,
  scheduledTime: string | null,
  timeZone?: string,
  gmtOffset?: string | null,
) => {
  const ctor = Intl.DateTimeFormat;
  if (utcToLocalDateCacheCtor !== ctor) {
    utcToLocalDateCache.clear();
    utcToLocalDateCacheCtor = ctor;
  }
  const key = `${scheduledDate}|${scheduledTime}|${timeZone}|${gmtOffset}`;
  const cached = utcToLocalDateCache.get(key);
  if (cached !== undefined) {
    return new Date(cached);
  }
  const result = computeUtcToLocalDate(scheduledDate, scheduledTime, timeZone, gmtOffset);
  if (utcToLocalDateCache.size > 50000) utcToLocalDateCache.clear();
  utcToLocalDateCache.set(key, result.getTime());
  return result;
};

const computeUtcToLocalDate = (
  scheduledDate: string,
  scheduledTime: string | null,
  timeZone?: string,
  gmtOffset?: string | null,
) => {
  if (!scheduledTime) {
    return DateTime.fromISO(scheduledDate).toJSDate();
  }

  if (!gmtOffset) {
    return new Date(`${scheduledDate} ${scheduledTime} UTC`);
  }

  const newUTCDate = DateTime.fromISO(`${scheduledDate}T${scheduledTime}`, { zone: 'UTC' });

  const localActionDate = newUTCDate.setZone(timeZone);

  // Modify the regex to also accept minutes in the GMT format, for example "GMT-05:30"
  const match = gmtOffset.match(/^GMT([+-])(\d{2})(?::(\d{2}))?$/);

  if (!match) throw new Error('Invalid GMT format');

  const sign = match[1] === '+' ? 1 : -1;
  const hoursOffset = parseInt(match[2], 10);
  const minutesOffset = match[3] ? parseInt(match[3], 10) : 0;

  // Calculate the total offset in minutes, including hours and minutes
  const offsetStored = sign * (hoursOffset * 60 + minutesOffset);

  const currentOffset = localActionDate.offset;

  let adjustedDateTime = localActionDate;

  if (currentOffset !== offsetStored) {
    const offsetDifference = offsetStored - currentOffset;
    adjustedDateTime = localActionDate.plus({ minutes: offsetDifference });
  }

  return adjustedDateTime.toJSDate();
};

/**
 * Returns the calendar date (yyyy-MM-dd) of an action in its timezone.
 * Use this when assigning events to week-day columns so late-evening times
 * (e.g. Sunday 6 PM MST) stay on the correct day instead of the UTC date (Monday).
 */
// Memoized for the same reason as utcToLocalDate below: pure string->string, luxon
// zone lookups per call, runs per occurrence per render.
const localDateStringCache = new Map<string, string>();
let localDateStringCacheCtor: unknown;

export const getLocalDateStringInTimezone = (
  scheduledDate: string,
  scheduledTime: string | null,
  timeZone?: string,
  gmtOffset?: string | null,
): string => {
  if (!scheduledTime) {
    return scheduledDate;
  }
  if (!timeZone || !gmtOffset) {
    return scheduledDate;
  }
  const ctor = Intl.DateTimeFormat;
  if (localDateStringCacheCtor !== ctor) {
    localDateStringCache.clear();
    localDateStringCacheCtor = ctor;
  }
  const key = `${scheduledDate}|${scheduledTime}|${timeZone}|${gmtOffset}`;
  const cached = localDateStringCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const result = computeLocalDateStringInTimezone(scheduledDate, scheduledTime, timeZone, gmtOffset);
  if (localDateStringCache.size > 50000) localDateStringCache.clear();
  localDateStringCache.set(key, result);
  return result;
};

const computeLocalDateStringInTimezone = (
  scheduledDate: string,
  scheduledTime: string,
  timeZone: string,
  gmtOffset: string,
): string => {
  const utc = DateTime.fromISO(`${scheduledDate}T${scheduledTime}`, { zone: 'UTC' });
  if (!utc.isValid) return scheduledDate;
  const local = utc.setZone(timeZone);
  if (!local.isValid) return scheduledDate;
  const match = gmtOffset.match(/^GMT([+-])(\d{2})(?::(\d{2}))?$/);
  if (!match) return scheduledDate;
  const sign = match[1] === '+' ? 1 : -1;
  const hoursOffset = parseInt(match[2], 10);
  const minutesOffset = match[3] ? parseInt(match[3], 10) : 0;
  const offsetStored = sign * (hoursOffset * 60 + minutesOffset);
  const adjusted = local.offset !== offsetStored ? local.plus({ minutes: offsetStored - local.offset }) : local;
  const result = adjusted.toFormat('yyyy-MM-dd');
  return result === 'Invalid DateTime' ? scheduledDate : result;
};

// Intl.DateTimeFormat construction is very expensive and these run per occurrence of every
// timed recurring action on busy pages. Caches are keyed on the Intl.DateTimeFormat
// constructor identity so tests that swap in a fresh mock get fresh values.
let currentTimezoneCache: { ctor: unknown; timeZone: string } | undefined;
const getCurrentTimezone = () => {
  const ctor = Intl.DateTimeFormat;
  if (currentTimezoneCache?.ctor !== ctor) {
    currentTimezoneCache = { ctor, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  }
  return currentTimezoneCache.timeZone;
};

const longOffsetFormatterCache = new Map<string, { ctor: unknown; formatter: Intl.DateTimeFormat }>();
const getLongOffsetFormatter = (timeZone: string) => {
  const ctor = Intl.DateTimeFormat;
  let entry = longOffsetFormatterCache.get(timeZone);
  if (entry?.ctor !== ctor) {
    entry = { ctor, formatter: new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' }) };
    longOffsetFormatterCache.set(timeZone, entry);
  }
  return entry.formatter;
};

// Memoized like utcToLocalDate: pure given the input and current timezone, but each
// call builds luxon DateTimes and runs toFormat twice — and it runs per occurrence per
// render (instance keys, completion lookups) on timed recurring actions.
const localDateToUTCCache = new Map<string | number, { scheduledDate: string; scheduledTime: string; gmtOffset: string }>();
let localDateToUTCCacheCtor: unknown;

export const localDateToUTC = (scheduledDateTime: string | Date) => {
  const ctor = Intl.DateTimeFormat;
  if (localDateToUTCCacheCtor !== ctor) {
    localDateToUTCCache.clear();
    localDateToUTCCacheCtor = ctor;
  }
  const key = scheduledDateTime instanceof Date ? scheduledDateTime.getTime() : scheduledDateTime;
  const cached = localDateToUTCCache.get(key);
  if (cached !== undefined) {
    return { ...cached };
  }
  const result = computeLocalDateToUTC(scheduledDateTime);
  if (localDateToUTCCache.size > 50000) localDateToUTCCache.clear();
  localDateToUTCCache.set(key, result);
  return { ...result };
};

const computeLocalDateToUTC = (scheduledDateTime: string | Date) => {
  let localDate: DateTime;
  const currentTimezone = getCurrentTimezone();

  if (scheduledDateTime instanceof Date) {
    localDate = DateTime.fromJSDate(scheduledDateTime, { zone: currentTimezone });
  } else if (typeof scheduledDateTime === 'string') {
    localDate = DateTime.fromFormat(scheduledDateTime, 'yyyy-MM-dd HH:mm', { zone: currentTimezone });
  } else {
    throw new Error('scheduledDateTime must be a string o Date');
  }

  if (!localDate.isValid) {
    throw new Error(`Invalid Date: ${scheduledDateTime}`);
  }

  const utcDate = localDate.toUTC();

  return {
    scheduledDate: utcDate.toFormat('yyyy-MM-dd'),
    scheduledTime: utcDate.toFormat('HH:mm:ss'),
    gmtOffset: getGMTOffset(currentTimezone, utcDate.toJSDate()),
  };
};

export const getGMTOffset = (timeZone: string, date?: Date) => {
  const longOffsetString = getLongOffsetFormatter(timeZone).format(date ?? new Date());
  return (longOffsetString.split(', ')[1] === 'GMT' ? 'GMT+00:00' : longOffsetString.split(', ')[1]) || 'GMT';
};

const parseGMTOffsetToMinutes = (offset?: string | null): number | null => {
  if (!offset) return null;
  if (offset === 'GMT') return 0;

  const match = offset.match(/^GMT([+-])(\d{2})(?::?(\d{2}))?$/);
  if (!match) return null;

  const [, sign, hours, minutes] = match;
  const totalMinutes = Number(hours) * 60 + Number(minutes ?? '0');

  return (sign === '-' ? -1 : 1) * totalMinutes;
};

type TimezoneContext = {
  timeZone?: string;
  gmtOffset?: string | null;
  fallbackTimeZone?: string;
};

const getZoneFromTimezoneData = ({ timeZone, gmtOffset, fallbackTimeZone }: TimezoneContext = {}) => {
  if (timeZone) return timeZone;

  const offsetMinutes = parseGMTOffsetToMinutes(gmtOffset);
  if (offsetMinutes !== null) {
    return FixedOffsetZone.instance(offsetMinutes);
  }

  return fallbackTimeZone;
};

const normalizeRecurrenceRuleUntil = (
  startDateISO: string | null | undefined,
  rule: RecurrenceRule,
  context?: TimezoneContext,
) => {
  if (!rule?.until || !startDateISO) {
    return rule;
  }

  const zone = getZoneFromTimezoneData(context) ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const scheduledDate = DateTime.fromISO(startDateISO, { zone });
  const untilDate = DateTime.fromISO(rule.until, { zone });

  if (!scheduledDate.isValid || !untilDate.isValid) {
    return rule;
  }

  const normalizedScheduledDate = scheduledDate.startOf('day');
  const normalizedUntilDate = untilDate.startOf('day');

  if (normalizedUntilDate.toMillis() < normalizedScheduledDate.toMillis()) {
    return { ...rule, until: undefined };
  }

  return rule;
};

type RecurrenceExceptionInput = {
  targetDate: string;
  scheduledTime?: string | null;
  timeZone?: string;
  gmtOffset?: string | null;
  existingExceptions?: { date: string }[];
  fallbackTimeZone?: string;
};

type RecurrenceExceptionResult = {
  untilUtc: string;
  exceptionUtc: string;
  exceptions: { date: string }[];
};

const computeRecurrenceExceptionDates = ({
  targetDate,
  scheduledTime,
  timeZone,
  gmtOffset,
  existingExceptions,
  fallbackTimeZone,
}: RecurrenceExceptionInput): RecurrenceExceptionResult => {
  const normalizedTimeRaw = scheduledTime ?? '00:00:00';
  const normalizedTime = normalizedTimeRaw.length === 5 ? `${normalizedTimeRaw}:00` : normalizedTimeRaw;

  const resolvedZone =
    getZoneFromTimezoneData({
      timeZone,
      gmtOffset,
      fallbackTimeZone: fallbackTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    }) ?? 'UTC';

  let localDateTime = DateTime.fromISO(`${targetDate}T${normalizedTime}`, { zone: resolvedZone });

  if (!localDateTime.isValid && gmtOffset) {
    const offset = gmtOffset.replace('GMT', '');
    localDateTime = DateTime.fromISO(`${targetDate}T${normalizedTime}${offset}`, { zone: 'UTC' });
  }

  if (!localDateTime.isValid) {
    localDateTime = DateTime.fromISO(`${targetDate}T${normalizedTime}`, { zone: 'UTC' });
  }

  const fallbackUntilDate = addDays(new Date(`${targetDate}T00:00:00`), -1);
  const fallbackUntil = Number.isNaN(fallbackUntilDate.getTime())
    ? targetDate
    : format(fallbackUntilDate, 'yyyy-MM-dd');

  // Use calendar dates in the user's zone, not UTC ISO dates. Converting the split instant with
  // toUTC().toISODate() can shift the calendar day vs the occurrence the user edited (e.g. late
  // evening or early morning in non-UTC zones), so the old series would still emit an instance on
  // the split day alongside the new series.
  const untilUtc = localDateTime.isValid
    ? localDateTime.minus({ days: 1 }).toFormat('yyyy-MM-dd')
    : fallbackUntil;
  const exceptionUtc = localDateTime.isValid ? localDateTime.toFormat('yyyy-MM-dd') : targetDate;

  const exceptionsArray = existingExceptions ?? [];
  const hasException = exceptionsArray.some((e) => e.date === exceptionUtc);
  const exceptions = hasException ? exceptionsArray : [...exceptionsArray, { date: exceptionUtc }];

  return {
    untilUtc,
    exceptionUtc,
    exceptions,
  };
};

export const localToGivenTimezoneDateToUTC = (scheduledDateTime: Date, timeZone: string) => {
  // time is given in the user's current timezone but should actually be selected timezone, so we need to swap it first
  const gmtOffset = getGMTOffset(timeZone);
  return localDateToUTC(new Date(`${format(scheduledDateTime, 'MMM dd yyyy HH:mm:ss')} ${gmtOffset ?? 'GMT'}`));
};

/**
 * Return a list of translated weekdays within a range
 * @param startDate
 * @param endDate
 * @param pattern (optional)
 */
const getDaysWithinDatesRange = (startDate: Date, endDate: Date): string[] => {
  const days = eachDayOfInterval({
    start: startDate,
    end: endDate,
  });

  return days.map((item) => format(item, 'yyyy-MM-dd HH:mm:ss'));
};

const formatDateLabelByPlan = (plan: typeof DAY | typeof WEEK, date: Date, locale: Locale = enUS) => {
  if (!plan) return;

  const currentYear = new Date().getFullYear();

  if (plan === DAY) {
    const showYear = date.getFullYear() !== currentYear;
    return format(date, showYear ? 'E, MMM dd yyyy' : 'E, MMM dd', { locale });
  }
  if (plan === WEEK) {
    const weekStart = startOfTheWeek(date);
    const weekEnd = endOfTheWeek(date);
    const showYear =
      weekStart.getFullYear() !== currentYear || weekEnd.getFullYear() !== currentYear;
    const fmt = showYear ? 'E dd yyyy' : 'E dd';
    return `${format(weekStart, fmt, { locale })} - ${format(weekEnd, fmt, { locale })}`;
  }
};

const filterActionsSameDay = (actions: Action[], selectedDate: Date) => {
  // Use the action's calendar date in its timezone so that e.g. Sunday 6 PM MST
  // matches the selected Sunday, not Monday (UTC date).
  const selectedDateStr = formatDateToString(selectedDate);
  return actions.filter((action) => {
    if (!action.scheduledDate) return false;
    if (action.scheduledTime && action.timezone && action.gmtOffset) {
      return getLocalDateStringInTimezone(
        action.scheduledDate,
        action.scheduledTime,
        action.timezone,
        action.gmtOffset,
      ) === selectedDateStr;
    }
    return action.scheduledDate === selectedDateStr;
  });
};

const filterActionsSameWeek = (actions: Action[], selectedDate: Date) => {
  // Actions can be out of bounds of the selectedDate due UTC midnights
  // when there is scheduledTime. The queries for Weekly plan should include
  // minus/plus 1 day and filter actions of same day using this function
  const weeklyPlanId = getWeeklyPlanId(selectedDate);

  if (!weeklyPlanId) {
    return [];
  }

  return actions.filter((action) => action.weeklyPlanId === weeklyPlanId);
};

const isThisWeek = (selectedDate: Date) => isThisWeekFns(selectedDate, { weekStartsOn: 1 });

const isThisMonth = (selectedDate: Date) => isThisMonthFns(selectedDate);

const isThisYear = (selectedDate: Date) => isThisYearFns(selectedDate);

const isPastWeek = (selectedDate: Date) => {
  const startDayOfNextWeek = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const startDayOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });

  return isBefore(startDayOfNextWeek, startDayOfCurrentWeek);
};

const isFutureWeek = (selectedDate: Date) => {
  const startDayOfNextWeek = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const startDayOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });

  return !isPastWeek(selectedDate) && isAfter(startDayOfNextWeek, startDayOfCurrentWeek);
};

const parseTimeToMinutes = (timeString: string): number => {
  const parts = timeString.split(' ');
  let totalMinutes = 0;

  for (const part of parts) {
    if (part.includes('h')) {
      const hours = parseInt(part.replace('h', ''), 10);
      totalMinutes += hours * 60;
    } else if (part.includes('m')) {
      const minutes = parseInt(part.replace('m', ''), 10);
      totalMinutes += minutes;
    }
  }

  return totalMinutes;
};

const getRepeatDatesFromScheduleWithoutRecurrence = (
  repeatSchedule: DuplicateActionScheduleType,
  actionScheduledDate: string,
  actionScheduledTime: string | null,
  timeZone?: string,
  gmtOffset?: string,
): { scheduledDate: string; scheduledTime: string | null; timeZone?: string; gmtOffset?: string }[] => {
  const repeatDate = utcToLocalDate(actionScheduledDate, actionScheduledTime, timeZone, gmtOffset);
  const repeatOnDates: Date[] = [];
  if (repeatSchedule.intervalType === 0) {
    // Day repeat
    for (let i = 0; i < repeatSchedule.repeatTimes; i++) {
      repeatDate.setDate(repeatDate.getDate() + 1);
      repeatOnDates.push(new Date(repeatDate));
    }
  } else if (repeatSchedule.intervalType === 1) {
    // Week repeat
    const actionDayOfWeekIndex = repeatDate.getDay();
    let lastScheduledWeekday = WEEKDAYS[actionDayOfWeekIndex];
    const weekdays = repeatSchedule.onWeekdays;
    const sortedWeekdays = WEEKDAYS.filter((weekday) => weekdays.includes(weekday));
    let nextWeekday = lastScheduledWeekday;
    for (let i = 0; i < repeatSchedule.repeatTimes; i++) {
      nextWeekday = sortedWeekdays[(sortedWeekdays.indexOf(nextWeekday) + 1) % sortedWeekdays.length];
      const nextWeekdayIndex = WEEKDAYS.indexOf(nextWeekday);
      const lastScheduledWeekdayIndex = WEEKDAYS.indexOf(lastScheduledWeekday);
      const daysToAdd =
        nextWeekdayIndex === lastScheduledWeekdayIndex
          ? WEEKDAYS.length
          : (WEEKDAYS.length + nextWeekdayIndex - lastScheduledWeekdayIndex) % WEEKDAYS.length;
      repeatDate.setDate(repeatDate.getDate() + daysToAdd);
      lastScheduledWeekday = nextWeekday;
      repeatOnDates.push(new Date(repeatDate));
    }
  }
  return repeatOnDates.map((date) => {
    if (actionScheduledTime) return localDateToUTC(date);
    return {
      scheduledDate: formatDateToString(date),
      scheduledTime: null,
      gmtOffset,
      timeZone,
    };
  });
};

const getRepeatDatesFromSchedule = (
  repeatSchedule: DuplicateActionScheduleType,
  actionScheduledDate: string,
  actionScheduledTime: string | null,
  timeZone?: string,
  gmtOffset?: string,
): { scheduledDate: string; scheduledTime: string | null; timeZone?: string; gmtOffset?: string }[] => {
  const repeatDate = utcToLocalDate(actionScheduledDate, actionScheduledTime, timeZone, gmtOffset);
  const repeatOnDates: Date[] = [];
  if (repeatSchedule.intervalType === 0) {
    repeatDate.setDate(repeatDate.getDate());
    repeatOnDates.push(new Date(repeatDate));
  }
  return repeatOnDates.map((date) => {
    if (actionScheduledTime) return localDateToUTC(date);
    return {
      scheduledDate: formatDateToString(date),
      scheduledTime: null,
      gmtOffset,
      timeZone,
    };
  });
};

const getWeekBoundariesForDates = (
  dates: { scheduledDate: string; scheduledTime: string | null; timeZone?: string; gmtOffset?: string }[],
) => {
  const acc: [Date, Date][] = [];
  return dates.reduce((acc, dateStruct) => {
    const realDate = utcToLocalDate(
      dateStruct.scheduledDate,
      dateStruct.scheduledTime,
      dateStruct.timeZone,
      dateStruct.gmtOffset,
    );
    const monday = startOfTheWeek(realDate);
    if (!acc.find((d) => isSameDay(d[0], monday))) {
      acc.push([monday, endOfTheWeek(realDate)]);
    }
    return acc;
  }, acc);
};

const getLocale = (language: string): Locale => {
  return locales[language] || enUS;
};

/**
 * Parse date string with timezone awareness
 * Uses formatStringToDate for date-only strings to avoid timezone shifts
 * Uses new Date() for ISO strings with timezone info
 */
const parseActionDate = (dateString: string): Date => {
  if (!dateString) return new Date();

  // If the string contains 'T' or 'Z', it's an ISO string with timezone info
  if (/[TZ]/.test(dateString)) {
    return new Date(dateString);
  }

  // Otherwise, it's a date-only string, use formatStringToDate to avoid timezone issues
  return formatStringToDate(dateString);
};

/**
 * Convert a duration string to minutes
 * @param duration - The duration string to convert
 * @returns The duration in minutes
 */
const durationStringToMinutes = (duration: string): number => {
  const [hours, minutes] = duration.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Get the local time string for a given date and time zone
 * @param date - The date to get the local time string for
 * @param timeZone - The time zone to get the local time string for
 * @returns The local time string
 */
const getLocalTimeString = (date: Date | string, timeZone?: string): string => {
  const formatter = new Intl.DateTimeFormat([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  return formatter.format(typeof date === 'string' ? new Date(date) : date);
};

const formatDateToShortString = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export {
  calendarAdapterApi,
  checkIfIsAllDay,
  endOfTheWeek,
  externalCalendarAdapterApi,
  externalCalendarMyEventAdapterApi,
  getRepeatDatesFromSchedule,
  getWeekBoundariesForDates,
  blockEventAdapterApi,
  categoryEventAdapterApi,
  externalEventFormatDuration,
  filterActionsSameDay,
  formatDateLabelByPlan,
  formatDateToString,
  formatStringToDate,
  getDaysWithinDatesRange,
  getEventLength,
  getEventProvider,
  computeRecurrenceExceptionDates,
  getZoneFromTimezoneData,
  isFutureWeek,
  isPastWeek,
  isThisWeek,
  isThisMonth,
  isThisYear,
  locales,
  localizer,
  normalizeRecurrenceRuleUntil,
  minutesToDurationString,
  parseGMTOffsetToMinutes,
  providerIcon,
  startOfTheWeek,
  timeReadableConvert,
  utcToLocalDate,
  parseTimeToMinutes,
  filterActionsSameWeek,
  getRepeatDatesFromScheduleWithoutRecurrence,
  getLocale,
  parseActionDate,
  durationStringToMinutes,
  getLocalTimeString,
  formatDateToShortString,
};
